import React from 'react';
import PropTypes from "prop-types";
import {useMainLayout} from "./index";

import {LoadingOverlay} from "../../components/LoadingOverlay";
import {useTheme} from "@mui/material/styles";
import {styled} from "@mui/material";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MenuIcon from "@mui/icons-material/Menu";

const Main = styled('main')({
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
});

const Title = props => {
    const {title, subTitle, color='default'} = props;

    if (!subTitle) {
        return (
            <Typography variant="h5" component="div" sx={{flexGrow: 1}} color={color}>
                {title}
            </Typography>
        )
    }

    return (
        <Stack direction="column" spacing={0.75} sx={{flexGrow: 1}}>
            <Typography variant="h5" component="div" sx={{lineHeight: 1}} color={color}>
                {title}
            </Typography>
            <Typography variant="body2" component="div" sx={{lineHeight: 1}} color={color}>
                {subTitle}
            </Typography>
        </Stack>
    )
}

const Page = props => {
    const {title, children, disablePadding, hidden, loading, onBack, subTitle, actions, overlay} = props;
    const {isDesktop, handleSidebarOpen} = useMainLayout()
    const theme = useTheme();

    return (
        <Box sx={{
            minHeight: '100%',
            display: 'flex',
            flexDirection: 'column',

            /* This is needed for a better transaction when opening an overlay page.
             * Indeed the background page might require time to become hidden and its component are
             * still visible in the overlay page for some ms. The background color hide the background page
             * completely.
            */
            backgroundColor: theme.palette.background.default,

            /*
             * The following 2 lines hide a page that is hidden. We use the visibility: hidden and overflow: hidden to
             * achieve the same result of a display: none.
             * We didn't use display: none since we had an issue with firefox (changing the display to
             * none and then back to flex causes firefox to lose the scroll offset of the hidden component).
             */
            visibility: hidden ? 'hidden' : 'inherit',
            overflow: hidden ? 'hidden' : 'inherit',

            ...(overlay ? {
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
            } : {})
        }}>
            <AppBar position="sticky" color={isDesktop ? 'white' : 'primary'} elevation={2}>
                <Toolbar>
                    {
                        !!onBack &&
                        <IconButton color="inherit"
                                    onClick={onBack}
                                    sx={{mr: '10px'}}>
                            <ArrowBackIcon/>
                        </IconButton>
                    }
                    {
                        !isDesktop &&
                        !onBack &&
                        <IconButton color="inherit"
                                    onClick={handleSidebarOpen}
                                    sx={{mr: '10px'}}>
                            <MenuIcon/>
                        </IconButton>
                    }
                    <Title title={title} subTitle={subTitle}/>
                    {actions}
                </Toolbar>
            </AppBar>

            <LoadingOverlay isLoading={loading} size='large' transparent>
                <Main sx={{p: disablePadding ? 0 : {xs: 2, md: 4}}}>
                    {children}
                </Main>
            </LoadingOverlay>
        </Box>
    );
};

Page.propTypes = {
    title: PropTypes.string.isRequired,
    children: PropTypes.node,
    disablePadding: PropTypes.bool,
    hidden: PropTypes.bool,
    loading: PropTypes.bool,
    onBack: PropTypes.func,
    subTitle: PropTypes.string,
    actions: PropTypes.node,
};

export default Page;